import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import './FreshClubDateRoll.less';
import * as api from 'util/api';
import { StickyHeader } from 'components';
import STORES from 'config/stores';
import {
  Button,
  Select,
  DatePicker,
  Table,
  Modal,
  Tag,
  Alert,
  Progress,
  Switch,
  Descriptions,
  Tooltip,
  InputNumber,
} from 'antd';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  EyeOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(tz);

const { Option } = Select;

const TIMEZONE_MAP = {
  GPNZ: 'Pacific/Auckland',
  GPAU: 'Australia/Melbourne',
};

const RECHARGE_TIMEZONE = 'EST';
const CHARGE_HOUR_EST = {
  GPNZ: 8,
  GPAU: 0,
};

const ENABLE_TEST_MODE = false;
const DEFAULT_TEST_MODE = false;

const formatDate = (isoDate) => {
  if (!isoDate) return '—';
  const parts = isoDate.split('-');
  if (parts.length !== 3) return isoDate;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const formatDateShort = (isoDate) => {
  if (!isoDate) return '—';
  const parts = isoDate.split('-');
  if (parts.length !== 3) return isoDate;
  return `${parts[2]}/${parts[1]}`;
};

const LegendContent = () => (
  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
    <li>
      <Tag icon={<SyncOutlined />} color="warning" style={{ marginRight: 4 }}>
        Ready to change
      </Tag>
      &mdash; These subscriptions have a next charge date within the look-ahead window and will be moved to the new target date.
    </li>
    <li style={{ marginTop: 6 }}>
      <Tag icon={<CheckCircleOutlined />} color="default" style={{ marginRight: 4 }}>
        Won&apos;t be changed
      </Tag>
      &mdash; These subscriptions have a next charge date beyond the look-ahead window. They belong to a future billing cycle and should not be moved.
    </li>
    <li style={{ marginTop: 6 }}>
      <Tag icon={<ExclamationCircleOutlined />} color="error" style={{ marginRight: 4 }}>
        Investigate
      </Tag>
      &mdash; These subscriptions have no scheduled charge date in Recharge. Click the row to view details and investigate in Recharge directly.
    </li>
    <li style={{ marginTop: 6 }}>
      <strong>Look-ahead</strong>
      {' '}
      &mdash; Controls how far into the future the tool will look for subscriptions to change.
      Set this to cover only the current billing cycle (e.g. 30 days).
      Increasing it will include subscriptions from later months; decreasing it will narrow the scope.
      The calendar next to the input shows the calculated cutoff date. Click Save to persist the value &mdash; it is shared across all users for this store.
    </li>
  </ul>
);

const renderStatusTag = (status) => {
  if (status === 'running') return <Tag icon={<SyncOutlined spin />} color="processing">Running</Tag>;
  if (status === 'completed') return <Tag icon={<CheckCircleOutlined />} color="success">Completed</Tag>;
  if (status === 'failed') return <Tag icon={<CloseCircleOutlined />} color="error">Failed</Tag>;
  return <Tag>{status}</Tag>;
};

const buildCompletionDescription = (roll, onViewMissed) => (
  <span>
    {'Updated '}
    {roll.subscriptionsUpdated}
    {' subscriptions, unchanged '}
    {roll.subscriptionsSkipped}
    {', failed '}
    {roll.subscriptionsFailed}
    {'. Onetimes: '}
    {roll.onetimesUpdated}
    {' updated, '}
    {roll.onetimesFailed}
    {' failed. '}
    <Button type="link" onClick={onViewMissed} style={{ padding: 0 }}>
      View missed subscribers
    </Button>
  </span>
);

const FreshClubDateRoll = ({ defaultStore }) => {
  const [store, setStore] = useState(defaultStore);
  const [selectedDate, setSelectedDate] = useState(null);
  const [testMode, setTestMode] = useState(DEFAULT_TEST_MODE);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [activeRoll, setActiveRoll] = useState(null);
  const [starting, setStarting] = useState(false);
  const [missedModalRollId, setMissedModalRollId] = useState(null);
  const [missedData, setMissedData] = useState(null);
  const [loadingMissed, setLoadingMissed] = useState(false);
  const [subscribersModal, setSubscribersModal] = useState(null);
  const [legendOpen, setLegendOpen] = useState(false);
  const [historyDetail, setHistoryDetail] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [lookAheadDays, setLookAheadDays] = useState(30);
  const [savingConfig, setSavingConfig] = useState(false);
  const pollRef = useRef(null);
  const summaryCache = useRef({});
  const configCache = useRef({});

  const localTimezone = TIMEZONE_MAP[store];
  const countryLabel = store === 'GPNZ' ? 'New Zealand' : 'Australia';
  const countryCode = store === 'GPNZ' ? 'NZ' : 'AU';

  const lookAheadDate = dayjs().add(lookAheadDays, 'day');

  const fetchConfig = useCallback(async (storeId) => {
    try {
      const config = await api.getFreshClubConfig(storeId);
      configCache.current[storeId] = config.lookAheadDays;
      setLookAheadDays(config.lookAheadDays);
    } catch (e) {
      // handled by api helper
    }
  }, []);

  const fetchSummary = useCallback(async (storeId) => {
    setLoadingSummary(true);
    try {
      const data = await api.getDateRollSummary(storeId);
      summaryCache.current[storeId] = data;
      setSummary(data);
    } catch (e) {
      // handled by api helper
    }
    setLoadingSummary(false);
  }, []);

  useEffect(() => {
    setSelectedDate(null);
    setActiveRoll(null);
    setSubscribersModal(null);

    if (summaryCache.current[store]) {
      setSummary(summaryCache.current[store]);
    } else {
      setSummary(null);
    }

    if (configCache.current[store] !== undefined) {
      setLookAheadDays(configCache.current[store]);
    } else {
      setLookAheadDays(30);
    }

    fetchConfig(store);
    fetchSummary(store);
  }, [store, fetchConfig, fetchSummary]);

  const handleSaveLookAhead = async (days) => {
    setSavingConfig(true);
    try {
      await api.updateFreshClubConfig(store, days);
      configCache.current[store] = days;
      setLookAheadDays(days);
      fetchSummary(store);
    } catch (e) {
      // handled by api helper
    }
    setSavingConfig(false);
  };

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const data = await api.getDateRollHistory();
      setHistory(data);
      const running = data.find((r) => r.status === 'running');
      if (running) setActiveRoll(running);
    } catch (e) {
      // handled by api helper
    }
    setLoadingHistory(false);
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Poll active roll status
  useEffect(() => {
    if (!activeRoll || activeRoll.status !== 'running') {
      if (pollRef.current) clearInterval(pollRef.current);
      return undefined;
    }

    pollRef.current = setInterval(async () => {
      try {
        const data = await api.getDateRollStatus(activeRoll.key);
        setActiveRoll(data);
        if (data.status !== 'running') {
          clearInterval(pollRef.current);
          fetchHistory();
          fetchSummary(store);
        }
      } catch (e) {
        // keep polling
      }
    }, 3000);

    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeRoll?.key, activeRoll?.status, fetchHistory]);

  const getConvertedDates = () => {
    if (!selectedDate) return null;
    const chargeHour = CHARGE_HOUR_EST[store] ?? 8;
    const userDateStr = selectedDate.format('YYYY-MM-DD');

    // Find the EST date whose charge fires on the user's selected local date
    let est = null;
    const offsets = [0, -1, 1];
    for (let i = 0; i < offsets.length; i += 1) {
      const candidateStr = dayjs(userDateStr).add(offsets[i], 'day').format('YYYY-MM-DD');
      const candidate = dayjs.tz(candidateStr, RECHARGE_TIMEZONE)
        .hour(chargeHour).minute(0).second(0);
      if (candidate.tz(localTimezone).format('YYYY-MM-DD') === userDateStr) {
        est = candidate;
        break;
      }
    }

    if (!est) {
      est = dayjs.tz(userDateStr, RECHARGE_TIMEZONE)
        .hour(chargeHour).minute(0).second(0);
    }

    return {
      localDisplay: selectedDate.format('dddd D MMMM YYYY'),
      estDate: est.format('YYYY-MM-DD'),
      estDisplay: est.format('dddd D MMMM YYYY, h:mm A [EST]'),
      localChargeTime: est.tz(localTimezone).format('dddd D MMMM YYYY, h:mm A'),
    };
  };

  const convertedDates = getConvertedDates();

  const handleStartRoll = async () => {
    if (!selectedDate || !convertedDates) return;

    const confirmContent = (
      <div>
        <p>
          {'You are about to roll all active FRESH Club subscription charge dates for '}
          <strong>{countryLabel}</strong>
          .
        </p>
        <Descriptions column={1} size="small" bordered style={{ marginTop: 12 }}>
          <Descriptions.Item label="Selected date (local)">
            {convertedDates.localDisplay}
          </Descriptions.Item>
          <Descriptions.Item label="Recharge date (EST)">
            {convertedDates.estDisplay}
          </Descriptions.Item>
          <Descriptions.Item label="Charges process at (local)">
            {convertedDates.localChargeTime}
          </Descriptions.Item>
        </Descriptions>
        {testMode && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
            message="Test mode is ON — only subscriptions for kyle@garageproject.co.nz and websales@garageproject.co.nz will be affected."
          />
        )}
      </div>
    );

    Modal.confirm({
      title: 'Confirm Date Roll',
      width: 520,
      content: confirmContent,
      okText: 'Roll Dates',
      okType: 'primary',
      onOk: async () => {
        setStarting(true);
        try {
          const result = await api.startDateRoll(
            store, selectedDate.format('YYYY-MM-DD'), testMode,
          );
          setActiveRoll({
            key: result.rollId,
            status: 'running',
            countryId: store,
            targetDateEst: result.targetDateEst,
            targetDateLocal: result.targetDateLocal,
            subscriptionsTotal: 0,
            subscriptionsUpdated: 0,
            subscriptionsSkipped: 0,
            subscriptionsFailed: 0,
            onetimesUpdated: 0,
            onetimesFailed: 0,
          });
          setSelectedDate(null);
        } catch (e) {
          // handled by api helper
        }
        setStarting(false);
      },
    });
  };

  const handleViewMissed = async (rollId) => {
    setMissedModalRollId(rollId);
    setLoadingMissed(true);
    setMissedData(null);
    try {
      const data = await api.getDateRollMissed(rollId);
      setMissedData(data);
    } catch (e) {
      // handled by api helper
    }
    setLoadingMissed(false);
  };

  const exportMissedCsv = () => {
    if (!missedData?.subscribers?.length) return;
    const headers = ['Customer Name', 'Email', 'Subscription ID', 'Next Charge (EST)', 'Next Charge (Local)', 'Variant', 'Recharge URL'];
    const rows = missedData.subscribers.map((s) => [
      s.customerName, s.customerEmail, s.subscriptionId,
      s.nextChargeDateEst, s.nextChargeDateLocal, s.variantTitle, s.rechargeUrl,
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((v) => `"${(v || '').toString().replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `missed-subscribers-roll-${missedModalRollId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const processed = activeRoll
    ? activeRoll.subscriptionsUpdated + activeRoll.subscriptionsSkipped + activeRoll.subscriptionsFailed
    : 0;
  const progressPercent = activeRoll?.subscriptionsTotal
    ? Math.round((processed / activeRoll.subscriptionsTotal) * 100)
    : 0;

  const historyColumns = [
    {
      title: 'Store',
      dataIndex: 'countryId',
      width: 80,
      render: (v) => (<Tag>{v}</Tag>),
      filters: [{ text: 'GPNZ', value: 'GPNZ' }, { text: 'GPAU', value: 'GPAU' }],
      onFilter: (value, record) => record.countryId === value,
    },
    {
      title: 'Target Date (EST)',
      dataIndex: 'targetDateEst',
      width: 140,
      render: (v) => formatDate(v),
    },
    {
      title: 'Target Date (Local)',
      dataIndex: 'targetDateLocal',
      width: 140,
      render: (v) => formatDate(v),
    },
    {
      title: 'Rolled By',
      dataIndex: 'rolledByEmail',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Rolled At',
      dataIndex: 'rolledAt',
      width: 180,
      render: (v) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—'),
    },
    {
      title: 'Updated',
      dataIndex: 'subscriptionsUpdated',
      width: 80,
      render: (v) => (<Tag color="green">{v}</Tag>),
    },
    {
      title: 'Unchanged',
      dataIndex: 'subscriptionsSkipped',
      width: 80,
      render: (v) => (v > 0 ? <Tag color="orange">{v}</Tag> : <Tag>{v}</Tag>),
    },
    {
      title: 'Failed',
      dataIndex: 'subscriptionsFailed',
      width: 80,
      render: (v) => (v > 0 ? <Tag color="red">{v}</Tag> : <Tag>{v}</Tag>),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (v) => renderStatusTag(v),
    },
    {
      title: 'Actions',
      width: 120,
      render: (_, record) => (record.status === 'completed' ? (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={(e) => { e.stopPropagation(); handleViewMissed(record.key); }}
        >
          Missed
        </Button>
      ) : null),
    },
  ];

  const missedColumns = [
    { title: 'Customer', dataIndex: 'customerName', width: 180 },
    {
      title: 'Email',
      dataIndex: 'customerEmail',
      width: 220,
      ellipsis: true,
    },
    { title: 'Subscription ID', dataIndex: 'subscriptionId', width: 130 },
    {
      title: 'Next Charge (EST)',
      dataIndex: 'nextChargeDateEst',
      width: 140,
      render: (v) => formatDate(v),
    },
    {
      title: 'Next Charge (Local)',
      dataIndex: 'nextChargeDateLocal',
      width: 150,
      render: (v) => formatDate(v),
    },
    { title: 'Variant', dataIndex: 'variantTitle', width: 140 },
    {
      title: 'Recharge',
      dataIndex: 'rechargeUrl',
      width: 100,
      render: (url) => (url ? <a href={url} target="_blank" rel="noopener noreferrer">View</a> : '—'),
    },
  ];

  const isRolling = activeRoll?.status === 'running';

  const dateLabel = `Target date (${countryCode} local time):`;
  const missedModalTitle = (
    <span>
      <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
      {`Missed Subscribers — Roll #${missedModalRollId}`}
    </span>
  );

  return (
    <div id="fresh-club-date-roll-container">
      <StickyHeader id="fresh-club-date-roll-header">
        <Select
          value={store}
          style={{ width: 200 }}
          onChange={setStore}
          disabled={isRolling}
        >
          <Option value={STORES.GPNZ}>Garage Project NZ</Option>
          <Option value={STORES.GPAU}>Garage Project AU</Option>
        </Select>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => { fetchSummary(store); fetchHistory(); }}
          loading={loadingSummary || loadingHistory}
        >
          Refresh
        </Button>
      </StickyHeader>

      <div className="date-roll-content">
        {/* Subscription Summary */}
        <div className="date-roll-summary-section">
          <h3>
            <TeamOutlined />
            {` Current Subscriptions — ${countryLabel}`}
          </h3>
          {loadingSummary && <p>Loading subscription data...</p>}
          {summary && !loadingSummary && (
            <div>
              <div className="summary-controls-inline">
                <Tooltip title="Subscriptions with a next charge date beyond this many days from today will be skipped during a roll. This setting is shared across all users.">
                  <span className="date-roll-label">Look-ahead:</span>
                </Tooltip>
                <InputNumber
                  min={1}
                  max={365}
                  value={lookAheadDays}
                  onChange={(val) => { if (val) setLookAheadDays(val); }}
                  style={{ width: 70 }}
                  size="small"
                  disabled={isRolling}
                />
                <span className="look-ahead-unit">days</span>
                <DatePicker
                  value={lookAheadDate}
                  format="DD/MM/YYYY"
                  disabled
                  size="small"
                  style={{ width: 130 }}
                />
                <Button
                  size="small"
                  type="primary"
                  onClick={() => handleSaveLookAhead(lookAheadDays)}
                  loading={savingConfig}
                  disabled={isRolling}
                >
                  Save
                </Button>
              </div>
              <div className="summary-table-row">
                <div style={{ flex: '0 0 auto' }}>
                  <Button
                    className="legend-button-mobile"
                    icon={<InfoCircleOutlined />}
                    size="small"
                    onClick={() => setLegendOpen(true)}
                    style={{ marginBottom: 8 }}
                  >
                    Show legend
                  </Button>
                  <Table
                    dataSource={summary.dateGroups}
                    rowKey={(record) => record.dateEst || 'no-date'}
                    pagination={false}
                    size="small"
                    style={{ cursor: 'pointer' }}
                    onRow={(record) => ({
                      onClick: () => setSubscribersModal(record),
                    })}
                    rowClassName={(record) => {
                      if (record.noScheduledDate) return 'summary-row-no-date';
                      if (record.willRoll) return 'summary-row-affected';
                      return 'summary-row-skipped';
                    }}
                    columns={[
                      {
                        title: 'Next Charge (EST)',
                        dataIndex: 'dateEst',
                        className: 'hide-on-mobile',
                        width: 150,
                        render: (v, record) => {
                          if (record.noScheduledDate) return <span className="no-date-label">No scheduled date</span>;
                          return formatDate(v);
                        },
                      },
                      {
                        title: `Next Charge (${countryCode})`,
                        dataIndex: 'dateLocal',
                        width: 150,
                        render: (v, record) => {
                          if (record.noScheduledDate) return '—';
                          return (
                            <>
                              <span className="hide-on-mobile">{formatDate(v)}</span>
                              <span className="show-on-mobile">{formatDateShort(v)}</span>
                            </>
                          );
                        },
                      },
                      {
                        title: 'Subscriptions',
                        dataIndex: 'count',
                        width: 110,
                        render: (v, record) => {
                          if (record.noScheduledDate) return <Tag color="red">{v}</Tag>;
                          return <Tag color={record.willRoll ? 'orange' : 'default'}>{v}</Tag>;
                        },
                      },
                      {
                        title: 'Status',
                        width: 130,
                        render: (_, record) => {
                          if (record.noScheduledDate) {
                            return (
                              <Tag icon={<ExclamationCircleOutlined />} color="error">
                                Investigate
                              </Tag>
                            );
                          }
                          if (record.willRoll) {
                            return (
                              <Tag icon={<SyncOutlined />} color="warning">
                                Ready to change
                              </Tag>
                            );
                          }
                          return (
                            <Tag icon={<CheckCircleOutlined />} color="default">
                              Won&apos;t be changed
                            </Tag>
                          );
                        },
                      },
                    ]}
                  />
                </div>
                <Alert
                  className="legend-alert-desktop"
                  type="info"
                  showIcon
                  style={{ flex: '1 1 300px', alignSelf: 'flex-start' }}
                  message="How to read this table"
                  description={<LegendContent />}
                />
              </div>
            </div>
          )}
        </div>

        {/* Date Roll Form */}
        <div className="date-roll-form-section">
          <h3>
            <CalendarOutlined />
            {' Roll Charge Dates'}
          </h3>

          <div className="date-roll-form">
            <div className="date-roll-input-row">
              <span className="date-roll-label">{dateLabel}</span>
              <DatePicker
                value={selectedDate}
                onChange={setSelectedDate}
                format="DD/MM/YYYY"
                disabled={isRolling}
                style={{ width: 200 }}
              />
            </div>

            {ENABLE_TEST_MODE && (
              <div className="date-roll-input-row">
                <span className="date-roll-label">Test mode:</span>
                <Tooltip title="When enabled, only subscriptions for kyle@garageproject.co.nz and websales@garageproject.co.nz will be processed">
                  <Switch
                    checked={testMode}
                    onChange={setTestMode}
                    disabled={isRolling}
                    checkedChildren="ON"
                    unCheckedChildren="OFF"
                  />
                </Tooltip>
              </div>
            )}

            {convertedDates && (
              <div className="date-roll-preview">
                <Descriptions column={1} size="small" bordered>
                  <Descriptions.Item label="Selected date (local)">
                    {convertedDates.localDisplay}
                  </Descriptions.Item>
                  <Descriptions.Item label="Recharge date (EST)">
                    {convertedDates.estDisplay}
                  </Descriptions.Item>
                  <Descriptions.Item label="Charges process at (local)">
                    {convertedDates.localChargeTime}
                  </Descriptions.Item>
                </Descriptions>
              </div>
            )}

            <Button
              type="primary"
              onClick={handleStartRoll}
              disabled={!selectedDate || isRolling}
              loading={starting}
              size="large"
              style={{ marginTop: 16 }}
            >
              Roll Dates
            </Button>
          </div>
        </div>

        {/* Active Roll Progress */}
        {isRolling && (
          <div className="date-roll-progress-section">
            <h3>
              <SyncOutlined spin />
              {` Roll in Progress — ${activeRoll.countryId}`}
            </h3>
            <p>
              {'Rolling to EST '}
              {formatDate(activeRoll.targetDateEst)}
              {' (local '}
              {formatDate(activeRoll.targetDateLocal)}
              )
            </p>
            <Progress percent={progressPercent} status="active" />
            <div className="date-roll-progress-stats">
              <Tag color="green">{`Updated: ${activeRoll.subscriptionsUpdated}`}</Tag>
              <Tag color="orange">{`Unchanged: ${activeRoll.subscriptionsSkipped}`}</Tag>
              <Tag color="red">{`Failed: ${activeRoll.subscriptionsFailed}`}</Tag>
              <Tag>{`Total: ${activeRoll.subscriptionsTotal}`}</Tag>
              <Tag color="blue">{`Onetimes updated: ${activeRoll.onetimesUpdated}`}</Tag>
            </div>
          </div>
        )}

        {/* Completion Summary */}
        {activeRoll && activeRoll.status === 'completed' && (
          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 24 }}
            message="Date roll completed"
            description={buildCompletionDescription(
              activeRoll,
              () => handleViewMissed(activeRoll.key),
            )}
            closable
            onClose={() => setActiveRoll(null)}
          />
        )}

        {activeRoll && activeRoll.status === 'failed' && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 24 }}
            message="Date roll failed"
            description={activeRoll.errorMessage || 'An unexpected error occurred.'}
            closable
            onClose={() => setActiveRoll(null)}
          />
        )}

        {/* Roll History */}
        <div className="date-roll-history-section">
          <h3>Roll History</h3>
          <Table
            dataSource={history}
            columns={historyColumns}
            rowKey="key"
            loading={loadingHistory}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1200 }}
            size="small"
            onRow={(record) => ({
              onClick: () => setHistoryDetail(record),
              style: { cursor: 'pointer' },
            })}
          />
        </div>
      </div>

      {/* Missed Subscribers Modal */}
      <Modal
        title={missedModalTitle}
        visible={missedModalRollId !== null}
        onCancel={() => { setMissedModalRollId(null); setMissedData(null); }}
        footer={[
          <Button key="export" icon={<DownloadOutlined />} onClick={exportMissedCsv} disabled={!missedData?.subscribers?.length}>
            Export CSV
          </Button>,
          <Button key="close" type="primary" onClick={() => { setMissedModalRollId(null); setMissedData(null); }}>
            Close
          </Button>,
        ]}
        width={1100}
      >
        {missedData && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={`Showing ${missedData.total} subscribers with a next charge date between ${missedData.windowStart} and ${missedData.windowEnd} (EST)`}
            description="These subscribers were not rolled to the target date. Some may have legitimately later dates due to delays or skips — review manually."
          />
        )}
        <Table
          dataSource={missedData?.subscribers || []}
          columns={missedColumns}
          rowKey="subscriptionId"
          loading={loadingMissed}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1000 }}
          size="small"
        />
      </Modal>

      {/* Subscribers Detail Modal */}
      <Modal
        title={
          subscribersModal?.noScheduledDate
            ? 'Subscriptions with no scheduled date'
            : `Subscriptions charged on ${formatDate(subscribersModal?.dateEst)} EST`
        }
        visible={subscribersModal !== null}
        onCancel={() => setSubscribersModal(null)}
        footer={(
          <Button type="primary" onClick={() => setSubscribersModal(null)}>
            Close
          </Button>
        )}
        width={700}
      >
        {subscribersModal?.noScheduledDate && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="These subscriptions have no next_charge_scheduled_at value in Recharge."
            description="This typically means the subscription has been paused, skipped indefinitely, or has a data issue. Click through to Recharge to investigate."
          />
        )}
        <Table
          dataSource={subscribersModal?.subscribers || []}
          rowKey="subscriptionId"
          pagination={{ pageSize: 20 }}
          size="small"
          columns={[
            {
              title: 'Subscription ID',
              dataIndex: 'subscriptionId',
              width: 130,
            },
            {
              title: 'Customer ID',
              dataIndex: 'customerId',
              width: 120,
            },
            {
              title: 'Variant',
              dataIndex: 'variantTitle',
              width: 200,
              ellipsis: true,
            },
            {
              title: 'Recharge',
              dataIndex: 'rechargeUrl',
              width: 100,
              render: (url) => (
                <a href={url} target="_blank" rel="noopener noreferrer">View</a>
              ),
            },
          ]}
        />
      </Modal>

      {/* Legend Modal (mobile) */}
      <Modal
        title="How to read this table"
        visible={legendOpen}
        onCancel={() => setLegendOpen(false)}
        footer={(
          <Button type="primary" onClick={() => setLegendOpen(false)}>
            Close
          </Button>
        )}
        width={520}
      >
        <LegendContent />
      </Modal>

      {/* Roll History Detail Modal */}
      <Modal
        title={`Roll #${historyDetail?.key || ''}`}
        visible={historyDetail !== null}
        onCancel={() => setHistoryDetail(null)}
        footer={(
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {historyDetail?.status === 'completed' && (
              <Button
                icon={<EyeOutlined />}
                onClick={() => {
                  handleViewMissed(historyDetail.key);
                  setHistoryDetail(null);
                }}
              >
                View missed subscribers
              </Button>
            )}
            <Button type="primary" onClick={() => setHistoryDetail(null)}>
              Close
            </Button>
          </div>
        )}
        width={480}
      >
        {historyDetail && (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Store">
              <Tag>{historyDetail.countryId}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Target Date (EST)">
              {formatDate(historyDetail.targetDateEst)}
            </Descriptions.Item>
            <Descriptions.Item label="Target Date (Local)">
              {formatDate(historyDetail.targetDateLocal)}
            </Descriptions.Item>
            <Descriptions.Item label="Rolled By">
              {historyDetail.rolledByEmail || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Rolled At">
              {historyDetail.rolledAt
                ? dayjs(historyDetail.rolledAt).format('DD/MM/YYYY HH:mm')
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {renderStatusTag(historyDetail.status)}
            </Descriptions.Item>
            <Descriptions.Item label="Subscriptions Updated">
              <Tag color="green">
                {historyDetail.subscriptionsUpdated}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Subscriptions Unchanged">
              <Tag color={historyDetail.subscriptionsSkipped > 0 ? 'orange' : 'default'}>
                {historyDetail.subscriptionsSkipped}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Subscriptions Failed">
              <Tag color={historyDetail.subscriptionsFailed > 0 ? 'red' : 'default'}>
                {historyDetail.subscriptionsFailed}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Onetimes Updated">
              <Tag color="blue">
                {historyDetail.onetimesUpdated}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Onetimes Failed">
              <Tag color={historyDetail.onetimesFailed > 0 ? 'red' : 'default'}>
                {historyDetail.onetimesFailed}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default FreshClubDateRoll;
